import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

// Each topic carries its own mode:
//   edit — everything is editable (authoring your answers beforehand)
//   live — read-only; mutating controls are hidden so nothing can be changed
//          or deleted by a stray click while you are mid-interview.
//
// Per topic rather than global, because you are typically still writing some
// topics while others are already interview-ready. Persisted, so a topic you
// left in live mode is still in live mode when the call starts.

const STORAGE_KEY = "topicModes";

const ModeContext = createContext();

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const ModeProvider = ({ children }) => {
  const [modes, setModes] = useState(readStored);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(modes));
    } catch {
      // Storage unavailable (private mode); modes still work for this session.
    }
  }, [modes]);

  // Topics default to edit mode, so a newly created topic is immediately
  // editable rather than appearing to have missing controls.
  const isEditMode = useCallback(
    (topicId) => modes[String(topicId)] !== false,
    [modes]
  );

  const toggleEditMode = useCallback((topicId) => {
    const key = String(topicId);
    setModes((current) => ({ ...current, [key]: current[key] === false }));
  }, []);

  return (
    <ModeContext.Provider value={{ isEditMode, toggleEditMode }}>
      {children}
    </ModeContext.Provider>
  );
};

export const useMode = () => {
  const context = useContext(ModeContext);
  if (!context) {
    throw new Error("useMode must be used within a ModeProvider");
  }
  return context;
};
